import React, { useState } from "react";
import axios from "../../utils/axiosConfig";
import { useLookup } from "../../utils/useLookup";
import { formatReportDate as formatDate } from "../../utils/reportDate";
import "./DealStatusReport.css";

const formatNumber = (value, decimals = 2) => Number(value || 0).toLocaleString("en-IN", {
  minimumFractionDigits: decimals,
  maximumFractionDigits: decimals
});

const DealStatusReport = () => {
  const { suppliers, buyers } = useLookup();
  const [dealOption, setDealOption] = useState("all");
  const [filterOption, setFilterOption] = useState("all");
  const [selectedId, setSelectedId] = useState("");
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const handleGenerate = async () => {
    try {
      setLoading(true);
      setError("");
      const response = await axios.post("/report/deal-status", {
        dealOption,
        filterOption,
        selectedId: filterOption !== "all" && selectedId ? Number(selectedId) : null
      });
      setReport(response.data);
    } catch (err) {
      setReport(null);
      setError(err.response?.data?.message || "Failed to generate report.");
    } finally {
      setLoading(false);
    }
  };

  const options = filterOption === "shipper" ? suppliers : buyers;
  const idKey = filterOption === "shipper" ? "ShipperID" : "BuyerID";
  const nameKey = filterOption === "shipper" ? "ShipperName" : "BuyerName";
  const partyLabel = filterOption === "shipper" ? "Supplier" : "Buyer";
  const company = report?.company || {};
  const totals = report?.totals || {};
  const deals = report?.deals || report?.parties?.flatMap((party) => party.deals) || [];
  const reportPartyLabel = report?.criteria.filterOption === "Shipper wise" ? "Supplier" : "Buyer";

  return (
    <div className="container-fluid mt-3 deal-status-page">
      <div className="card shadow-sm border-0 no-print">
        <div className="card-header bg-white">
          <span className="badge bg-success fs-6 px-3 py-2">Deal Status</span>
        </div>
        <div className="card-body">
          <div className="border rounded p-3 bg-light">
            <div className="row mb-2 align-items-start deal-status-filter-row">
              <label className="col-sm-2 text-end pt-1">Select Deal Option</label>
              <span className="col-auto px-1 pt-1">:</span>
              <div className="col-sm-4">
                {[
                  ["all", "All Deal"],
                  ["ongoing", "Ongoing Deal"]
                ].map(([value, label]) => (
                  <div className="form-check" key={value}>
                    <input
                      className="form-check-input"
                      type="radio"
                      id={`deal-option-${value}`}
                      checked={dealOption === value}
                      onChange={() => setDealOption(value)}
                    />
                    <label className="form-check-label" htmlFor={`deal-option-${value}`}>{label}</label>
                  </div>
                ))}
              </div>
            </div>

            <div className="row mb-2 align-items-start deal-status-filter-row">
              <label className="col-sm-2 text-end pt-1">Select Filter Option</label>
              <span className="col-auto px-1 pt-1">:</span>
              <div className="col-sm-4">
                {[
                  ["all", "All"],
                  ["shipper", "Shipper wise"],
                  ["buyer", "Buyer wise"]
                ].map(([value, label]) => (
                  <div className="form-check" key={value}>
                    <input
                      className="form-check-input"
                      type="radio"
                      id={`deal-filter-${value}`}
                      checked={filterOption === value}
                      onChange={() => {
                        setFilterOption(value);
                        setSelectedId("");
                      }}
                    />
                    <label className="form-check-label" htmlFor={`deal-filter-${value}`}>{label}</label>
                  </div>
                ))}
              </div>
            </div>

            {filterOption !== "all" && <div className="row mb-2 align-items-center deal-status-filter-row">
              <label className="col-sm-2 text-end">Select {partyLabel}</label>
              <span className="col-auto px-1">:</span>
              <div className="col-sm-4">
                <select className="form-select form-select-sm" value={selectedId} onChange={(e) => setSelectedId(e.target.value)}>
                  <option value="">All {partyLabel}</option>
                  {options.map((option) => (
                    <option key={option[idKey]} value={option[idKey]}>{option[nameKey]}</option>
                  ))}
                </select>
              </div>
            </div>

            }

            {error && <div className="alert alert-danger py-2 mt-3 mb-0">{error}</div>}

            <div className="row mt-3">
              <div className="col-sm-10 offset-sm-2 d-flex gap-2">
                <button className="btn btn-success btn-sm px-4" onClick={handleGenerate} disabled={loading}>
                  {loading ? "Generating..." : "Generate Report"}
                </button>
                {report && (
                  <button className="btn btn-outline-secondary btn-sm px-4" onClick={() => window.print()}>
                    Print / Save PDF
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {report && (
        <section className="deal-status-print mt-3">
          <header className="deal-status-header">
            <h1>{company.CompanyName || "Global Metals"}</h1>
            <h2>Deal Status Report</h2>
          </header>

          <div className="deal-status-meta">
            <span><strong>Deal:</strong> {report.criteria.dealOption}</span>
            <span><strong>Filter:</strong> {report.criteria.filterOption}</span>
            {report.criteria.filterOption !== "All" && <span><strong>{reportPartyLabel}:</strong> {report.criteria.selectedParty}</span>}
            <span><strong>Generated:</strong> {formatDate(report.criteria.generatedAt)}</span>
          </div>

          {deals.length === 0 ? (
            <div className="deal-status-empty">No records found.</div>
          ) : (
            <article className="deal-status-results">
              <table className="deal-status-table">
                <thead>
                  <tr>
                    <th>#</th>
                    <th>Deal No</th>
                    <th>Date</th>
                    <th>Supplier</th>
                    <th>Buyer</th>
                    <th>S.C No</th>
                    <th>Currency</th>
                    <th className="number">Qty</th>
                    <th className="number">Containers</th>
                    <th>Status</th>
                    <th>Pending At</th>
                    <th>Invoice / B.L.</th>
                    <th>Shipment</th>
                  </tr>
                </thead>
                <tbody>
                  {deals.flatMap((deal, index) => {
                    const shipments = deal.Shipments?.length ? deal.Shipments : [null];
                    return shipments.map((shipment, shipmentIndex) => (
                    <tr key={`${deal.DealID}-${shipment?.ShipmentID ?? "unscheduled"}`}>
                      {shipmentIndex === 0 && <>
                      <td rowSpan={shipments.length}>{index + 1}</td>
                      <td rowSpan={shipments.length} className="deal-number">{deal.DealNo || deal.DealID}</td>
                      <td rowSpan={shipments.length}>{formatDate(deal.DealDate)}</td>
                      <td rowSpan={shipments.length}>{deal.ShipperName || "-"}</td>
                      <td rowSpan={shipments.length}>{deal.BuyerName || "-"}</td>
                      <td rowSpan={shipments.length}>
                        {deal.SCNo || "-"}
                        {deal.SCDate && <small>{formatDate(deal.SCDate)}</small>}
                      </td>
                      <td rowSpan={shipments.length}>{deal.Currency || "-"}</td>
                      <td rowSpan={shipments.length} className="number">{formatNumber(deal.ShippedWeight, 3)}</td>
                      <td rowSpan={shipments.length} className="number">{formatNumber(deal.NoOfContainer, 0)}</td>
                      <td rowSpan={shipments.length}>{deal.DealStatusName}</td>
                      <td rowSpan={shipments.length}>{deal.PendingStatusName}</td>
                      </>}
                      <td>
                        {shipment ? shipment.InvoiceNo || "-" : deal.InvoiceNo || "-"}
                        {(shipment ? shipment.InvoiceDate : deal.InvoiceDate) && <small>Inv: {formatDate(shipment ? shipment.InvoiceDate : deal.InvoiceDate)}</small>}
                        {(shipment ? shipment.BLNo : deal.BLNo) && <small>B.L.: {shipment ? shipment.BLNo : deal.BLNo}</small>}
                      </td>
                      <td>
                        {shipment?.ContainerNo || "-"}
                        {shipment && <small>Net Weight: {formatNumber(shipment.NetWeight, 3)}</small>}
                        {shipment?.VesselName && <small>Vessel: {shipment.VesselName}</small>}
                        {shipment?.ShipLine && <small>Ship Line: {shipment.ShipLine}</small>}
                        {shipment?.ETD && <small>ETD: {formatDate(shipment.ETD)}</small>}
                        {shipment?.ETA && <small>ETA: {formatDate(shipment.ETA)}</small>}
                      </td>
                    </tr>
                    ));
                  })}
                </tbody>
              </table>
            </article>
          )}

          <footer className="deal-status-total">
            <strong>Grand Total</strong>
            <span>Parties: {totals.parties}</span>
            <span>Deals: {totals.deals}</span>
            <span>Containers: {formatNumber(totals.containers, 0)}</span>
            <span>Quantity: {formatNumber(totals.quantity, 3)}</span>
            <span>Shipped Weight: {formatNumber(totals.shippedWeight, 3)}</span>
            <span>Invoice Amount: {formatNumber(totals.invoiceAmount)}</span>
          </footer>
        </section>
      )}
    </div>
  );
};

export default DealStatusReport;
